import React, { FC, useContext, useEffect, useRef, useState } from 'react'
import './OTPCollectForm.scss'
import { Button, Checkbox, Form, FormInput, Icon } from '@connectcore/connect-components-react'
import { ALIGN_BUTTONS_AND_LINKS } from '~utils/featureFlags'
import { IUserContextType, UserContext } from '~utils/context/UserContext'
import {
  FORGEROCK_FORGOT_PASSWORD_STAGES,
  FORGEROCK_LOGIN_STAGES,
  INVALID_ONE_TIME_PASSWORD,
  LOGIN_FAILURE_SCD_ERROR,
  OTP_ATTEMPTS_EXCEEDED_MESSAGE,
  OTP_Label,
  OTP_REQUIRED_MESSAGE,
} from '~utils/constants'
import { CallbackType, StepType } from '@forgerock/javascript-sdk'
import { getErrorTemplate } from '~utils/helpers'
import { Link } from '@connectcore/connect-components-react'
import OtpSupprtForm from '~components/OtpSupprtForm/OtpSupprtForm'

interface IFormData {
  OTPInput: string | null
  rememberDevice: boolean
  feedback: string | null
}

const OTPCollectForm: FC = () => {
  const [formData, setFormData] = useState<IFormData>({
    OTPInput: null,
    rememberDevice: false,
    feedback: null,
  })
  const [error, setError] = useState('')
  const [showPass, setShowPass] = useState(false)
  const [otpChannelText, setOTPChannelText] = useState('An Email/SMS')
  const [apiError, setApiError] = useState(false)
  const [showOtpSupport, setShowOtpSupport] = useState(false)
  const { step, nextStep, showErr, userInputError, selectedOTPChannel, otpSupport, setOtpSupport } =
    useContext(UserContext) as IUserContextType
  let passwordCallBackFn
  let textInputCallBackFn
  let choiceCallBackFn

  const stage: string = step?.payload?.stage
  const otpCollectRef = useRef<any>(null)

  useEffect(() => {
    const getOTPChannelLabel = otpChannelType => {
      return otpChannelType === 'Text'
        ? 'A SMS'
        : otpChannelType === 'Voice'
        ? 'A call'
        : otpChannelType === 'Email'
        ? 'An email'
        : 'An email/SMS/call'
    }
    setOTPChannelText(getOTPChannelLabel(selectedOTPChannel))
  }, [])

  if (step?.getCallbacksOfType(CallbackType.PasswordCallback)?.length > 0)
    passwordCallBackFn = step?.getCallbackOfType(CallbackType.PasswordCallback)

  if (step?.getCallbacksOfType(CallbackType.TextInputCallback)?.length > 0)
    textInputCallBackFn = step?.getCallbackOfType(CallbackType.TextInputCallback)

  const selectOTPChannel = () => {
    setOtpSupport(false)
    passwordCallBackFn?.setInputValue('GoBack')
    textInputCallBackFn?.setInputValue('select-otp-channel')
    nextStep(step)
  }

  const validateAndSetFormData = e => {
    setError('')
    setShowOtpSupport(false)
    setApiError(false)
    let feedback
    if (Object.keys(e).includes('OTPInput') && !e?.OTPInput?.trim()) {
      feedback = OTP_REQUIRED_MESSAGE
      setFormData({ ...formData, feedback: OTP_REQUIRED_MESSAGE })
    } else if (Object.keys(e).includes('OTPInput') || Object.keys(e).includes('rememberDevice')) {
      feedback = null
    }
    setFormData({
      ...formData,
      ...e,
      feedback: feedback,
    })
  }
  if (otpSupport) {
    if (step?.getCallbacksOfType(CallbackType.ChoiceCallback)?.length > 0) {
      choiceCallBackFn = step?.getCallbackOfType(CallbackType.ChoiceCallback)
      let choiceCallbackValue
      JSON.parse(choiceCallBackFn.getOutputByName('choices')[0]).map(channel => {
        if (channel.type === 'CCHelpdesk') {
          choiceCallbackValue = channel.value
        }
      })
      choiceCallBackFn.setInputValue(choiceCallbackValue)
      nextStep(step)
      setOtpSupport(false)
    }
  }
  const submitOTPInput = () => {
    otpCollectRef.current.focus()
    if (formData.OTPInput) {
      const trimmedOtpInput = formData.OTPInput.trim()
      passwordCallBackFn?.setInputValue(trimmedOtpInput)
      formData.rememberDevice
        ? textInputCallBackFn?.setInputValue('otp-input-and-register-device_' + trimmedOtpInput)
        : textInputCallBackFn?.setInputValue('otp-input-and-no-register-device_' + trimmedOtpInput)
      nextStep(step)
    } else {
      setFormData({ ...formData, feedback: OTP_REQUIRED_MESSAGE })
    }
  }
  useEffect(() => {
    setApiError(showErr)
  }, [showErr])

  useEffect(() => {
    if (userInputError === INVALID_ONE_TIME_PASSWORD) {
      setFormData({ ...formData, feedback: getErrorTemplate(INVALID_ONE_TIME_PASSWORD) })
    } else {
      if (userInputError) {
        setError(OTP_ATTEMPTS_EXCEEDED_MESSAGE)
        setFormData({ ...formData, feedback: null })
      }
    }
  }, [userInputError])

  return (
    <>
      {showOtpSupport && stage === FORGEROCK_LOGIN_STAGES.LOGIN_COLLECT_OTP && (
        <OtpSupprtForm setShowOtpSupport={setShowOtpSupport} />
      )}
      {!showOtpSupport && (
        <Form
          className="OTPCollectForm d-flex flex-column wide-form padding-bottom-scrlbr"
          data-testid="OTPCollectForm"
          onChange={e => validateAndSetFormData(e)}
          onSubmit={() => {
            submitOTPInput()
          }}
          key="OTPCollectForm"
        >
          <div>
            {stage === FORGEROCK_LOGIN_STAGES.LOGIN_COLLECT_OTP && (
              <h2 className="fw-bold my-5 align-self-center">
                Step 3 - Enter your one-time access code
              </h2>
            )}

            {stage === FORGEROCK_FORGOT_PASSWORD_STAGES.FORGOT_PASSWORD_COLLECT_OTP && (
              <h2 className="fw-bold my-5 align-self-center">
                Password change - Step 3 Enter your one-time access code
              </h2>
            )}

            <p>
              {otpChannelText} is on its way with your one-time access code. Once you receive your
              code, enter it below to verify your identity, and click Next.
            </p>
          </div>
          <FormInput
            className="my-5 w-50"
            name="OTPInput"
            required={true}
            innerRef={otpCollectRef}
            label={OTP_Label}
            iconOverlay
            type={showPass ? 'text' : 'password'}
            feedback={<p role="alert">{formData.feedback}</p>}
            validationState={
              formData.feedback !== null && formData.feedback?.length > 0 ? 'invalid' : undefined
            }
            appendWith={
              <Icon
                className="p-0"
                name={showPass ? 'hide' : 'view-watchlist'}
                actionable
                onClick={() => {
                  setShowPass(!showPass)
                }}
                aria-label={showPass ? 'Hide passcode' : 'Show passcode'}
              />
            }
          />
          {error && (
            <p className="invalid-feedback" role="alert">
              {error}
            </p>
          )}
          {step?.type === StepType.Step && stage === FORGEROCK_LOGIN_STAGES.LOGIN_COLLECT_OTP && (
            <>
              <p>Register this computer? </p>
              <Checkbox name="rememberDevice">
                Yes, register this computer (not recommended for a public computer). Uncheck the box
                if you do not want to register this computer.
              </Checkbox>
            </>
          )}

          {apiError && (
            <p className="invalid-feedback" role="alert">
              {LOGIN_FAILURE_SCD_ERROR}
            </p>
          )}
          <br />
          <div className="d-flex flex-column">
            <Button kind="primary" className={`my-3 align-${ALIGN_BUTTONS_AND_LINKS}`}>
              Next
            </Button>
          </div>
          <br />
          <p>
            Haven&apos;t received your one-time access code? We send access codes immediately, but
            many factors may influence how fast you receive them. If you have chosen to receive the
            access code by email, please check your spam folder to determine if it was delivered
            there. If you have not received your one-time access code within 10 minutes, you can
            request a new code below, or contact customer care for help.
          </p>
          <br />
          <div className="d-flex flex-column">
            <Button
              kind="secondary"
              className={`my-3 p-2 align-${ALIGN_BUTTONS_AND_LINKS}`}
              onClick={() => selectOTPChannel()}
            >
              Request a new code
            </Button>
          </div>
          {stage === FORGEROCK_LOGIN_STAGES.LOGIN_COLLECT_OTP && (
            <div>
              <Link
                href="#"
                onClick={() => {
                  setShowOtpSupport(true)
                  setOtpSupport(true)
                }}
                className="my-3 p-2"
              >
                Didn&apos;t receive one-time access code
              </Link>
            </div>
          )}
        </Form>
      )}
    </>
  )
}
export default OTPCollectForm
